// Mini-stories — scripted Croatian dialogues that run as a sequence of
// presentation lines + occasional interactive challenges. Vocabulary is
// drawn from the A1 skills in skills.js. Grammar notes are inline so
// future edits don't accidentally break case agreement.
//
// Schema per line:
//   { speaker, hr, sv, en, challenge?: { kind, options?, blankWord? } }
// challenge.kind ∈ 'understand' | 'choose' | 'cloze'
//   understand: presentation only, advance on Continue
//   choose:    pick the correct Croatian line from options (3-4 strings)
//   cloze:     pick the blank word from options (3-4 strings)

// Story 1: pekara (at the bakery)
//
// Grammar notes (so future edits don't accidentally break agreement):
// • "Što želite?" — formal 2pl of "željeti" (to want), polite address.
// • "Jedan kruh" — accusative singular; for masc INANIMATE nouns accusative = nominative, so "jedan" stays "jedan" (not "jednog", which is the animate form).
// • "Veliki / mali" — colloquial definite-form masc nom adjectives agreeing with the implied "kruh". The grammatically conservative indefinite form would be "velik / malen", but "veliki ili mali" is the natural spoken form.
// • "Dva eura" — paucal: numbers 2/3/4 take the genitive singular form. "Euro" (m) → "eura". Croatia switched from kuna to euro in 2023, so "eura" is current.
// • "Hvala lijepa" — feminine of "lijep" agreeing with the implicit feminine "hvala" ("a pretty thanks"); fixed idiom.
const pekara = {
  id: 'pekara',
  emoji: '🥖',
  title:   { sv: 'På bageriet',                      en: 'At the bakery' },
  scene:   { sv: 'Du går in i en pekara i Split.',  en: 'You walk into a pekara in Split.' },
  characters: [
    { id: 'maja',  emoji: '👩🏻' },
    { id: 'baker', emoji: '👨🏼‍🍳' },
  ],
  unlocksAfterSkill: 'food',
  rewardXP: 25,
  rewardGems: 2,
  lines: [
    {
      speaker: 'baker',
      hr: 'Dobar dan! Što želite?',
      sv: 'God dag! Vad vill ni ha?',
      en: 'Good day! What would you like?',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Dobar dan. Jedan kruh, molim.',
      sv: 'God dag. En limpa bröd, tack.',
      en: 'Good day. One loaf of bread, please.',
      challenge: {
        kind: 'choose',
        options: [
          'Jedan kruh, molim.',
          'Jedna kava, molim.',
          'Hvala lijepa.',
        ],
      },
    },
    {
      speaker: 'baker',
      hr: 'Veliki ili mali?',
      sv: 'Stor eller liten?',
      en: 'Big or small?',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Veliki, molim.',
      sv: 'Stor, tack.',
      en: 'Big, please.',
      challenge: {
        // Tests masculine adjective agreement — "kruh" is masculine so
        // "veliki" wins over "velika" (fem) and "veliko" (n).
        kind: 'cloze',
        blankWord: 'Veliki',
        options: ['Veliki', 'Velika', 'Veliko', 'Mali'],
      },
    },
    {
      speaker: 'baker',
      hr: 'Evo. Dva eura, molim.',
      sv: 'Varsågod. Två euro, tack.',
      en: 'Here you go. Two euros, please.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Hvala lijepa. Doviđenja!',
      sv: 'Tack så mycket. Hej då!',
      en: 'Thank you very much. Goodbye!',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'baker',
      hr: 'Doviđenja!',
      sv: 'Hej då!',
      en: 'Goodbye!',
      challenge: { kind: 'understand' },
    },
  ],
};

// Story 2: kafić (at the café)
//
// Grammar notes:
// • "Izvolite" — polite "please / go ahead", imperative of "izvoljeti", standard waiter greeting.
// • "Jednu kavu" — feminine accusative singular: "kava" → "kavu", "jedna" → "jednu".
// • "Veliku ili malu?" — feminine acc adjectives agreeing with the implied "kavu". Always feminine here, unlike pekara.
// • "Sa šećerom" — preposition "s/sa" + instrumental: "šećer" (m) → "šećerom". The "sa" variant is used before sibilant clusters for ease of pronunciation.
// • "Bez šećera" — preposition "bez" + genitive: "šećer" → "šećera".
// • "U redu" — fixed expression "alright / OK", literally "in order" (locative).
const kafic = {
  id: 'kafic',
  emoji: '☕',
  title:   { sv: 'På kaféet',                        en: 'At the café' },
  scene:   { sv: 'Du sätter dig vid ett bord i ett kafić i Zagreb.', en: 'You sit down at a table in a kafić in Zagreb.' },
  characters: [
    { id: 'maja',    emoji: '👩🏻' },
    { id: 'konobar', emoji: '🧑🏻‍🍳' },
  ],
  unlocksAfterSkill: 'food',
  rewardXP: 25,
  rewardGems: 2,
  lines: [
    {
      speaker: 'konobar',
      hr: 'Dobro jutro! Izvolite.',
      sv: 'God morgon! Varsågod.',
      en: 'Good morning! Please.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Dobro jutro. Jednu kavu, molim.',
      sv: 'God morgon. En kaffe, tack.',
      en: 'Good morning. One coffee, please.',
      challenge: {
        kind: 'choose',
        options: [
          'Jednu kavu, molim.',
          'Jedan kruh, molim.',
          'Hvala, doviđenja.',
        ],
      },
    },
    {
      speaker: 'konobar',
      hr: 'Veliku ili malu?',
      sv: 'Stor eller liten?',
      en: 'Big or small?',
      challenge: {
        // Tests feminine adjective agreement vs. the masculine forms from pekara.
        kind: 'cloze',
        blankWord: 'malu',
        options: ['malu', 'mali', 'malo', 'mala'],
      },
    },
    {
      speaker: 'maja',
      hr: 'Malu, molim.',
      sv: 'Liten, tack.',
      en: 'Small, please.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'konobar',
      hr: 'Sa šećerom?',
      sv: 'Med socker?',
      en: 'With sugar?',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Ne, hvala. Bez šećera.',
      sv: 'Nej tack. Utan socker.',
      en: 'No, thanks. Without sugar.',
      challenge: {
        // "bez" governs the genitive — distractors test that the learner
        // doesn't grab the nominative or another case.
        kind: 'cloze',
        blankWord: 'šećera',
        options: ['šećera', 'šećer', 'šećerom', 'šećeri'],
      },
    },
    {
      speaker: 'konobar',
      hr: 'U redu. Dva eura.',
      sv: 'Det blir bra. Två euro.',
      en: 'Alright. Two euros.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Hvala lijepa!',
      sv: 'Tack så mycket!',
      en: 'Thank you very much!',
      challenge: { kind: 'understand' },
    },
  ],
};

// Story 3: obitelj (the family)
//
// Grammar notes:
// • "Ovo je …" — neuter demonstrative + 3sg "biti" for introductions ("This is …"); fixed pattern.
// • "Moj otac / moja majka" — possessives agree with gender of the noun: "otac" m → "moj", "majka" f → "moja".
// • "On / Ona se zove" — reflexive verb "zvati se" (to be called). 3sg "zove", 3pl "zovu", paired with "se".
// • "Imam jednog brata" — "imati" + accusative. "Brat" is masc ANIMATE, so acc = genitive form ("brata"); "jedan" likewise takes the animate acc "jednog".
// • "Imam i dvije sestre" — "dvije" is the feminine paucal of 2. Numbers 2/3/4 with feminine nouns trigger paucal: "sestra" → "sestre".
// • "One se zovu" — "one" = 3pl nom feminine pronoun, "zovu" = 3pl present of "zvati".
// • "Volim svoju obitelj" — "voljeti" + accusative; "svoj" is the reflexive possessive (used when the possessor is the sentence subject). "Obitelj" is a feminine i-stem, so accusative = nominative.
const obitelj = {
  id: 'obitelj',
  emoji: '👨‍👩‍👧‍👦',
  title:   { sv: 'Familjen',                  en: 'The family' },
  scene:   { sv: 'Maja visar dig sin familj.', en: 'Maja introduces her family.' },
  characters: [
    { id: 'maja', emoji: '👩🏻' },
  ],
  unlocksAfterSkill: 'family',
  rewardXP: 25,
  rewardGems: 2,
  lines: [
    {
      speaker: 'maja',
      hr: 'Ovo je moja obitelj.',
      sv: 'Det här är min familj.',
      en: 'This is my family.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Ovo je moj otac. On se zove Marko.',
      sv: 'Det här är min pappa. Han heter Marko.',
      en: 'This is my father. He is called Marko.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'Ovo je moja majka. Ona se zove Ana.',
      sv: 'Det här är min mamma. Hon heter Ana.',
      en: 'This is my mother. She is called Ana.',
      challenge: {
        // Tests the masculine vs feminine possessive ("moj" vs "moja").
        kind: 'cloze',
        blankWord: 'moja',
        options: ['moja', 'moj', 'moje', 'moji'],
      },
    },
    {
      speaker: 'maja',
      hr: 'Imam jednog brata. On se zove Ivan.',
      sv: 'Jag har en bror. Han heter Ivan.',
      en: 'I have one brother. He is called Ivan.',
      challenge: {
        // "Brat" is masc animate, so accusative = genitive ("brata").
        // Distractors: nominative, feminine paucal, accusative of "sestra".
        kind: 'choose',
        options: [
          'Imam jednog brata.',
          'Imam jednu sestru.',
          'Imam dvije sestre.',
        ],
      },
    },
    {
      speaker: 'maja',
      hr: 'Imam i dvije sestre.',
      sv: 'Jag har också två systrar.',
      en: 'I also have two sisters.',
      challenge: { kind: 'understand' },
    },
    {
      speaker: 'maja',
      hr: 'One se zovu Marta i Lucija.',
      sv: 'De heter Marta och Lucija.',
      en: 'They are called Marta and Lucija.',
      challenge: {
        // Verb-conjugation challenge: 3pl reflexive "zovu" vs other persons.
        kind: 'cloze',
        blankWord: 'zovu',
        options: ['zovu', 'zove', 'zovem', 'zoveš'],
      },
    },
    {
      speaker: 'maja',
      hr: 'Volim svoju obitelj.',
      sv: 'Jag älskar min familj.',
      en: 'I love my family.',
      challenge: { kind: 'understand' },
    },
  ],
};

export const stories = [pekara, kafic, obitelj];
